/**
 * The guide's entry point and single state owner — Fluent v9 rendition of references/ui-spec.md §1.
 *
 * Mounted ONCE, by components/AppHelp.tsx (the only file that touches the router and i18next):
 *   <HelpLauncher pathname={pathname} viewer={viewer} theme={webTheme} appLanguage={i18n.language} notify={toast} />
 *
 * Launcher, per the spec: DOCKED in the top bar, left of the notification bell. The top bar is
 * GrecoHeader (@greco/components), which takes no children, so the shell (AppHelp) inserts an
 * empty slot as the first child of its icon cluster and passes it in as `dockSlot`; the "?" is
 * portaled into it as the same Fluent v8 IconButton the header uses for its own icons. While a
 * dialog is open the header sits under the backdrop, so a fixed copy is rendered on top at the
 * docked button's rectangle (spec §1). Without a slot (header not rendered) the floating "?" is
 * the fallback, so the guide is always reachable.
 *
 * Everything renders in a body portal under its own FluentProvider, so the Fluent CSS variables
 * exist (the app's provider does not reach a body portal) and dark mode follows the app.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IconButton, mergeStyles } from "@fluentui/react";
import { FluentProvider, type Theme } from "@fluentui/react-components";
import { QuestionCircle24Regular } from "@fluentui/react-icons";
import { activeDialogSurface, HELP_PANEL_ATTR, isStepOnScreen } from "./dom";
import { HelpDrawer } from "./HelpDrawer";
import { HelpInspect } from "./HelpInspect";
import { HelpTour } from "./HelpTour";
import { readLanguagePref, writeLanguagePref } from "./preference";
import { helpForPath } from "./registry";
import { fmt, HELP_STRINGS } from "./strings";
import { HELP_TABSTER_ATTRS } from "./tabster";
import "./help.css";
import {
  localizedContext,
  localizedPage,
  localizedStep,
  stepVisibleForRole,
  type HelpLanguage,
  type HelpStep,
  type HelpViewer,
} from "./types";

/** A message the guide wants shown to the user, outside its own panels. */
export interface HelpNotice {
  severity: "info" | "warning" | "success";
  message: string;
}

/** The drawer's close transition — starting the tour sooner spotlights elements under the panel. */
const DRAWER_CLOSE_MS = 260;

const NO_ROLES: HelpViewer = {
  isManager: false,
  isAdmin: false,
  isSuperAdmin: false,
};

/** GrecoHeader's own icon-button style (its `iconStyle`), so the "?" matches the bell beside it. */
const DOCKED_CLASS = mergeStyles({
  height: "50px",
  width: "50px",
  color: "white",
  fontSize: "26px",
});
/** The header's blue, for the copy that stands on the dimmed header while a dialog is open. */
const HEADER_BLUE = "#005aa1";

/**
 * Whether a modal dialog is on screen. Watched, not measured once; state is set only on change,
 * because the observer hears every grid re-render.
 */
const useDialogOpen = (): boolean => {
  const [open, setOpen] = useState(false);
  const last = useRef(false);
  useEffect(() => {
    const read = () => {
      const next = activeDialogSurface() !== null;
      if (next === last.current) return;
      last.current = next;
      setOpen(next);
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  return open;
};

interface HelpLauncherProps {
  pathname: string;
  /** PCT's frontend has no role gating; every flag false shows every step. */
  viewer?: HelpViewer;
  /** The app's Fluent v9 theme, so the portaled guide matches (and follows dark mode). */
  theme: Theme;
  /** The guide language when the user has not picked one — the app's own language. */
  appLanguage: HelpLanguage;
  /** The app's toast host. Survives navigation, which the Ask tab's cross-page notices need. */
  notify: (notice: HelpNotice) => void;
  /** Where the docked "?" goes — a slot in the top bar's icon cluster; null → floating fallback. */
  dockSlot?: HTMLElement | null;
  /**
   * The user manual PDF per language. Offered twice: right of the docked "?" in the app's language
   * (docked only — no floating copy), and in the drawer's header in the guide's language.
   */
  userManualFor?: (language: HelpLanguage) => UserManualFile;
}

/** A downloadable user manual: where it is served and the name it is saved under. */
export interface UserManualFile {
  href: string;
  fileName: string;
}

export const HelpLauncher = ({
  pathname,
  viewer = NO_ROLES,
  theme,
  appLanguage,
  notify,
  dockSlot = null,
  userManualFor,
}: HelpLauncherProps) => {
  // Keyed to the route: navigating away closes the panel by definition.
  const [openFor, setOpenFor] = useState<string | null>(null);
  const [tour, setTour] = useState<{
    steps: HelpStep[];
    scope: HTMLElement | null;
    startIndex: number;
  } | null>(null);
  const [inspecting, setInspecting] = useState(false);
  /** null → follow the app's language (read once; afterwards only the drawer's toggle changes it). */
  const [language, setLanguage] = useState<HelpLanguage>(() =>
    readLanguagePref(appLanguage),
  );
  const s = HELP_STRINGS[language];

  const help = helpForPath(pathname);
  const roleSteps = useMemo(
    () =>
      help ? help.steps.filter((step) => stepVisibleForRole(step, viewer)) : [],
    [help, viewer],
  );

  const dialogOpen = useDialogOpen();
  const dockedRef = useRef<HTMLElement | null>(null);
  const [dockRect, setDockRect] = useState<DOMRect | null>(null);
  // The header is under the dialog backdrop: measure the docked button so a copy can stand on it.
  useEffect(() => {
    if (!dialogOpen || !dockSlot) {
      setDockRect(null);
      return undefined;
    }
    const measure = () => {
      if (dockedRef.current)
        setDockRect(dockedRef.current.getBoundingClientRect());
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [dialogOpen, dockSlot]);

  // An unregistered route has no guide and therefore no button.
  if (!help) return null;

  const open = openFor === pathname;
  const title = localizedPage(help, language).title;

  const chooseLanguage = (next: HelpLanguage) => {
    setLanguage(next);
    writeLanguagePref(next);
  };

  /** Open the tour — from the beginning, or at the step the reader picked. */
  const startTour = (from?: HelpStep) => {
    setOpenFor(null);
    setInspecting(false);
    setTour(null);
    window.setTimeout(() => {
      // A tour started over an open dialog stays INSIDE it; role-visible AND rendered.
      const surface = activeDialogSurface();
      const visible = roleSteps.filter((step) =>
        isStepOnScreen(step, surface ?? document),
      );
      if (visible.length === 0) {
        notify({
          severity: "info",
          message: surface ? s.noticeDialogEmpty : s.noticeNothing,
        });
        return;
      }
      const startIndex = from ? visible.indexOf(from) : 0;
      if (from && startIndex < 0) {
        const stepTitle = localizedStep(from, language).title;
        const hint = from.context
          ? localizedContext(help, from.context, language)
          : undefined;
        notify({
          severity: "info",
          message: hint
            ? fmt(s.noticeNotOnScreenContext, {
                title: stepTitle,
                context: hint,
              })
            : fmt(s.noticeNotOnScreen, { title: stepTitle }),
        });
        return;
      }
      setTour({
        steps: visible,
        scope: surface,
        startIndex: Math.max(startIndex, 0),
      });
    }, DRAWER_CLOSE_MS);
  };

  const startInspect = () => {
    setOpenFor(null);
    setTour(null);
    window.setTimeout(() => setInspecting(true), DRAWER_CLOSE_MS);
  };

  const docked = dockSlot !== null;
  const headerManual = userManualFor?.(appLanguage);
  const showFloating = !docked && !open && tour === null && !inspecting;
  const toggle = () => setOpenFor(open ? null : pathname);
  const launcherTitle = fmt(s.launcherTooltip, { title });
  const launcherAria = fmt(s.launcherAria, { title });

  return (
    <>
      {docked &&
        createPortal(
          <>
            <IconButton
              elementRef={(el: HTMLElement | null) => {
                dockedRef.current = el;
              }}
              className={DOCKED_CLASS}
              iconProps={{ iconName: "Help" }}
              checked={open}
              title={launcherTitle}
              ariaLabel={launcherAria}
              onClick={toggle}
              // Behind a dialog's backdrop it cannot be clicked; the elevated copy below stands in.
              style={dockRect ? { visibility: "hidden" } : undefined}
              aria-hidden={dockRect ? true : undefined}
              tabIndex={dockRect ? -1 : undefined}
              {...{ [HELP_PANEL_ATTR]: "launcher" }}
              data-help="app-help-launcher"
            />
            {headerManual && (
              // One portal for both buttons, so the book always sits right of the "?".
              <IconButton
                className={DOCKED_CLASS}
                iconProps={{ iconName: "ReadingMode" }}
                href={headerManual.href}
                download={headerManual.fileName}
                title={HELP_STRINGS[appLanguage].userManual}
                ariaLabel={HELP_STRINGS[appLanguage].userManual}
                data-help="app-user-manual"
              />
            )}
          </>,
          dockSlot,
        )}
      {createPortal(
        <FluentProvider theme={theme} style={{ background: "transparent" }}>
          {docked && dockRect && (
            <IconButton
              className={DOCKED_CLASS}
              iconProps={{ iconName: "Help" }}
              checked={open}
              title={launcherTitle}
              ariaLabel={launcherAria}
              onClick={toggle}
              styles={{
                root: {
                  position: "fixed",
                  top: dockRect.top,
                  left: dockRect.left,
                  zIndex: 1000001,
                  background: HEADER_BLUE,
                  boxShadow: "0 2px 10px rgba(15, 23, 42, 0.35)",
                },
                rootHovered: { background: HEADER_BLUE, color: "white" },
              }}
              {...{ [HELP_PANEL_ATTR]: "launcher" }}
              {...HELP_TABSTER_ATTRS}
            />
          )}
          {showFloating && (
            <button
              type="button"
              className="pct-help-fab"
              title={launcherTitle}
              aria-label={launcherAria}
              onClick={() => setOpenFor(pathname)}
              {...{ [HELP_PANEL_ATTR]: "launcher" }}
              {...HELP_TABSTER_ATTRS}
              data-help="app-help-launcher"
            >
              <QuestionCircle24Regular />
            </button>
          )}

          <HelpDrawer
            open={open}
            help={help}
            steps={roleSteps}
            userManual={userManualFor?.(language)}
            language={language}
            onLanguageChange={chooseLanguage}
            notify={notify}
            onClose={() => setOpenFor(null)}
            onStartTour={startTour}
            onInspect={startInspect}
          />

          {tour && (
            <HelpTour
              key={`${tour.startIndex}-${tour.steps.length}`}
              help={help}
              steps={tour.steps}
              scope={tour.scope}
              startIndex={tour.startIndex}
              language={language}
              onInspect={() => {
                setTour(null);
                setInspecting(true);
              }}
              onClose={() => setTour(null)}
            />
          )}

          {inspecting && (
            <HelpInspect
              help={help}
              steps={roleSteps}
              language={language}
              onTourFrom={startTour}
              onExit={() => setInspecting(false)}
            />
          )}
        </FluentProvider>,
        document.body,
      )}
    </>
  );
};
